import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { InlineEditorConfig } from '../../base-editor/inline-editor.types';
import { InlineDateComponent } from '../date/inline-date.component';
import { InlineTimeComponent } from '../time/inline-time.component';

@UntilDestroy()
@Component({
  selector: 'blaze-inline-date-time',
  templateUrl: './inline-date-time.component.html',
  styleUrls: ['../datetime-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InlineDateComponent, InlineTimeComponent],
})
export class InlineDateTimeComponent implements AfterViewInit {
  config = input<InlineEditorConfig>();
  value = input<unknown>();

  valueChanged = output<string | null>();

  cellDateComponent = viewChild<InlineDateComponent>('inlineDateComponent');
  cellTimeComponent = viewChild<InlineTimeComponent>('inlineTimeComponent');

  private readonly valueTransformed = linkedSignal<{ date: string | null; time: string | null }>(
    () => {
      const value = this.value();
      if (value === null || value === undefined || !value) {
        return { date: null, time: null };
      }
      if (typeof value !== 'string') {
        throw new TypeError(
          `CellDateTimeComponent: value is expected to be string | null, got ${typeof value}`,
        );
      }

      const dateParts = value.split('T');
      return {
        date: dateParts[0],
        time: dateParts[1],
      };
    },
  );

  private readonly linkedValue = linkedSignal<string | null>(() => {
    const value = this.value();
    if (typeof value === 'string') {
      return value;
    } else if (value === null || value === undefined) {
      return null;
    }
    throw new TypeError(
      `CellDateTimeComponent: value is expected to be string | null, got ${typeof value}`,
    );
  });

  protected dateValue = computed<string | null>(() => {
    const valueParts = this.valueTransformed();
    const value = valueParts.date;
    if (value === null || value === undefined || !value) {
      return null;
    }

    let dateString = '';
    if (valueParts.time) {
      const date = new Date(value + 'T' + valueParts.time || '00:00:00');
      const dateDate = date.getDate().toString().padStart(2, '0');
      const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      const dateYear = date.getFullYear().toString();
      dateString = `${dateYear}-${dateMonth}-${dateDate}`;
    } else {
      dateString = value;
    }

    return dateString;
  });

  protected timeValue = computed<string | null>(() => {
    const valueParts = this.valueTransformed();
    const value = valueParts.time;
    if (value === null || value === undefined || !value) {
      return null;
    }

    let timeString = '';
    if (valueParts.date) {
      const date = new Date(valueParts.date + 'T' + value);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      timeString = `${hours}:${minutes}`;
    } else {
      timeString = value.slice(0, 5);
    }

    return timeString;
  });

  get isDatePickerOpened(): boolean {
    return this.cellDateComponent()?.datepicker()?.opened || false;
  }

  ngAfterViewInit(): void {
    this.cellDateComponent()?.openDatePicker();
  }

  protected dateUpdated(value: string | null): void {
    const oldValue = this.linkedValue();
    if (!value) {
      this.emitValueChanged(null);
      return;
    }
    const newDate = oldValue ? new Date(oldValue) : new Date(0, 0, 0, 0, 0, 0, 0);
    const dateParts = value.split('-').map(Number);
    newDate.setFullYear(dateParts[0], dateParts[1] - 1, dateParts[2]);

    this.linkedValue.set(newDate.toISOString());

    this.emitValueChanged(newDate.toISOString());
  }

  protected timeUpdated(value: string | null): void {
    const oldValue = this.linkedValue();
    if (!value) {
      this.emitValueChanged(null);
      return;
    }
    const newTime = oldValue ? new Date(oldValue) : new Date(0, 0, 0, 0, 0, 0, 0);
    const timeParts = value.split(':').map(Number);
    newTime.setHours(timeParts[0], timeParts[1]);

    this.linkedValue.set(newTime.toISOString());

    this.emitValueChanged(newTime.toISOString());
  }

  protected datePickerOpened(): void {
    this.cellTimeComponent()?.closeTimePicker();
  }

  protected timePickerOpened(): void {
    this.cellDateComponent()?.closeDatePicker();
  }

  private emitValueChanged(value: string | null): void {
    this.valueChanged.emit(value);
  }
}
